import React from "react";
import { AutoComplete, Button, Col, Input, InputNumber, Row, Select, Typography } from "antd";
import { DeleteOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  centsToEuro,
  decimalInputProps,
  euroToCents,
  formatEuro,
  formatQuantity
} from "../workReports/format";

const UNITS = ["Stk", "m", "m²", "m³", "lfm", "kg", "l", "psch", "Std."];

const Field = ({ label, children }) => (
  <label className="co-field">
    <span className="co-field-label">{label}</span>
    {children}
  </label>
);

/**
 * One Nachtrag position. "Price from the main offer" copies a main-offer position's unit and
 * unit price (and its text when empty) and remembers it as the base position.
 */
const PositionLine = ({
  index,
  position,
  computed,
  basePositions,
  readOnly,
  lng,
  onChange,
  onRemove
}) => {
  const { t } = useTranslation("dashboard");
  const set = patch => onChange({ ...position, ...patch });

  const pickBase = id => {
    if (!id) return set({ basePosition: null });
    const base = basePositions.find(p => String(p._id) === String(id));
    if (!base) return;
    set({
      basePosition: base._id,
      unit: base.unit || position.unit,
      unitPrice: base.unitPrice,
      description: position.description || base.description
    });
  };

  return (
    <div className="co-line">
      <div className="co-line-head">
        <Typography.Text strong>
          {t("Pos.")} {index + 1}
        </Typography.Text>
        <span className="co-line-figures">
          {computed && (
            <strong className={`co-num${computed.net < 0 ? " co-negative" : ""}`}>
              {formatEuro(computed.net, lng)}
            </strong>
          )}
        </span>
        {!readOnly && (
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            aria-label={t("Remove")}
            onClick={onRemove}
          />
        )}
      </div>
      <Row gutter={[8, 8]}>
        {basePositions.length > 0 && (
          <Col xs={24}>
            <Field label={t("Price from the main offer")}>
              <Select
                className="co-full"
                allowClear
                showSearch
                optionFilterProp="label"
                disabled={readOnly}
                value={position.basePosition || undefined}
                placeholder={t("Optional: continue the price of a contract position")}
                options={basePositions.map((p, i) => ({
                  value: p._id,
                  label: `${i + 1}. ${p.description} – ${formatEuro(p.unitPrice, lng)}/${p.unit || t("unit")}`
                }))}
                onChange={pickBase}
              />
            </Field>
          </Col>
        )}
        <Col xs={24}>
          <Field label={t("Description")}>
            <Input.TextArea
              value={position.description}
              disabled={readOnly}
              autoSize={{ minRows: 1, maxRows: 6 }}
              maxLength={2000}
              onChange={event => set({ description: event.target.value })}
            />
          </Field>
        </Col>
        <Col xs={12} md={6}>
          <Field label={t("Quantity")}>
            <InputNumber
              className="co-full"
              precision={3}
              inputMode="decimal"
              value={position.quantity}
              disabled={readOnly}
              {...decimalInputProps(lng)}
              onChange={value => set({ quantity: value || 0 })}
            />
          </Field>
        </Col>
        <Col xs={12} md={6}>
          <Field label={t("Unit")}>
            <AutoComplete
              className="co-full"
              value={position.unit}
              disabled={readOnly}
              options={UNITS.map(unit => ({ value: unit }))}
              filterOption={(input, option) =>
                option.value.toLowerCase().startsWith(String(input).toLowerCase())
              }
              onChange={unit => set({ unit: String(unit || "").slice(0, 20) })}
            />
          </Field>
        </Col>
        <Col xs={12} md={6}>
          <Field label={t("Unit price")}>
            <InputNumber
              className="co-full"
              min={0}
              precision={2}
              addonAfter="€"
              inputMode="decimal"
              value={centsToEuro(position.unitPrice)}
              disabled={readOnly}
              {...decimalInputProps(lng)}
              onChange={value => set({ unitPrice: euroToCents(value) })}
            />
          </Field>
        </Col>
        <Col xs={12} md={6} className="co-line-total">
          {computed && (
            <Typography.Text type="secondary">
              {formatQuantity(position.quantity, lng)} × {formatEuro(position.unitPrice, lng)}
            </Typography.Text>
          )}
        </Col>
      </Row>
      {position.quantity < 0 && (
        <Typography.Text type="secondary" className="co-hint">
          {t("Negative quantity: this work is left out and reduces the contract value.")}
        </Typography.Text>
      )}
    </div>
  );
};

export default PositionLine;
